import type { UploadSessionFacts } from "../../../types/uploadEvidence.js";
import type { UploadObjectFence, UploadObjectPartDescriptor } from "./uploadObjectManifest";
import { isValidBrowserSignedHeaders, type UploadTargetEnvelope } from "./targetInitiation";
import { isValidPresignedUrl, isValidEtag } from "./multipartValidation";

export interface UploadCapabilities {
	contractVersions: number[];
	preferredContractVersion: 1 | 2;
	v2: {
		newUploadsAllowed: boolean;
		kinds: string[];
		sourceModes: ("known-size" | "open")[];
		requiredTransport: "chunked-object";
		checksumAlgorithms: string[];
		limits: Record<
			| "minNonFinalPartBytes"
			| "maxPartBytes"
			| "maxObjectBytes"
			| "maxPartsPerObject"
			| "maxObjectsPerAdmission"
			| "maxPartDescriptorsPerAdmission"
			| "maxTargetsPerRequest"
			| "maxObjectsPerPage"
			| "maxPartsPerPage"
			| "maxAdmissionBodyBytes",
			number
		>;
		domainByteLimit: number;
	};
}
export interface UploadV2Facts extends UploadSessionFacts {
	discardReason: string | null;
}
export interface UploadV2Snapshot extends UploadObjectFence {
	id: string;
	/** Domain Snapshot identity, separate from the upload registry and local capture identity. */
	snapshotId?: string | null;
	contractVersion: 2;
	transport: "chunked-object";
	sourceMode: "known-size" | "open";
	inventoryRevision: number;
	transferVerified: boolean;
	sourceSeal: Record<string, unknown> | null;
	facts: UploadV2Facts;
	processingWork?: {
		phase: "publication" | "domain";
		state: "queued" | "running" | "held" | "failed" | "complete";
	};
}
export interface UploadV2ObjectMapping {
	clientObjectId: string;
	objectId: string;
	manifestPending: boolean;
}
export interface UploadV2ObjectTarget extends UploadTargetEnvelope {
	objectId: string;
}
const uuid = (value: unknown): value is string => typeof value === "string" && /^[a-f0-9]{8}-(?:[a-f0-9]{4}-){3}[a-f0-9]{12}$/i.test(value);
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
function invalid(reason: string): never {
	throw new Error(`Invalid upload v2 response: ${reason}`);
}
function validateSourceSeal(seal: unknown, mode: unknown, revision: number, verified: boolean): void {
	if (seal === null) {
		if (verified) invalid("verified transfer has no accepted source seal");
		return;
	}
	if (
		!record(seal) ||
		!uuid(seal.finalizationId) ||
		!Number.isSafeInteger(seal.inventoryRevision) ||
		(seal.inventoryRevision as number) < 0 ||
		(seal.inventoryRevision as number) > revision ||
		!record(seal.sourceSummary)
	)
		invalid("source seal identity");
	const summary = seal.sourceSummary;
	if (!Number.isSafeInteger(summary.totalBytes) || (summary.totalBytes as number) < 0) invalid("source seal byte total");
	if (
		mode === "known-size"
			? summary.kind !== "known-size" || summary.objectCount !== 1 || summary.totalBytes === 0
			: summary.kind !== "recording" ||
				!Number.isSafeInteger(summary.runCount) ||
				(summary.runCount as number) < 1 ||
				!Number.isSafeInteger(summary.fragmentCount) ||
				(summary.fragmentCount as number) < 0 ||
				!["confirmed", "recovered"].includes(summary.finality as string)
	)
		invalid("source seal summary");
}
export function parseUploadV2Snapshot(value: unknown, expected?: UploadObjectFence & { uploadId: string }): UploadV2Snapshot {
	if (
		!record(value) ||
		!record(value.attempt) ||
		value.contractVersion !== 2 ||
		value.transport !== "chunked-object" ||
		!uuid(value.id) ||
		!uuid(value.attempt.id) ||
		!Number.isSafeInteger(value.attempt.generation) ||
		(value.attempt.generation as number) < 1 ||
		typeof value.takeGeneration !== "string" ||
		!value.takeGeneration ||
		value.attempt.takeGeneration !== value.takeGeneration ||
		value.attempt.transport !== value.transport ||
		!["known-size", "open"].includes(value.sourceMode as string) ||
		!Number.isSafeInteger(value.inventoryRevision) ||
		(value.inventoryRevision as number) < 0 ||
		typeof value.transferVerified !== "boolean" ||
		(value.sourceSeal !== null && !record(value.sourceSeal)) ||
		!record(value.facts)
	)
		invalid("missing identity or independent server facts");
	if (
		(value.attemptId !== undefined && value.attemptId !== value.attempt.id) ||
		(value.generation !== undefined && value.generation !== value.attempt.generation)
	)
		invalid("inconsistent attempt fence");
	for (const key of ["transferState", "terminalIntent", "processingState", "recoverabilityState", "outputQuality"])
		if (typeof value.facts[key] !== "string" || !value.facts[key]) invalid("missing server fact");
	if (value.facts.discardReason !== null && typeof value.facts.discardReason !== "string") invalid("invalid discard reason");
	validateSourceSeal(value.sourceSeal, value.sourceMode, value.inventoryRevision as number, value.transferVerified);
	if (value.snapshotId != null && !uuid(value.snapshotId)) invalid("snapshot identity");
	if (
		value.processingWork !== undefined &&
		(!record(value.processingWork) ||
			value.transferVerified !== true ||
			!["publication", "domain"].includes(value.processingWork.phase as string) ||
			!["queued", "running", "held", "failed", "complete"].includes(value.processingWork.state as string) ||
			(value.processingWork.state === "failed" && value.facts.outputQuality !== "failed") ||
			(value.processingWork.state === "complete" &&
				(value.processingWork.phase !== "domain" || value.facts.outputQuality !== "complete")))
	)
		invalid("processing work");
	if (
		expected &&
		(value.id !== expected.uploadId ||
			value.attempt.id !== expected.attemptId ||
			value.attempt.generation !== expected.generation ||
			value.takeGeneration !== expected.takeGeneration)
	)
		invalid("response belongs to another attempt");
	return {
		id: value.id,
		...(value.snapshotId !== undefined ? { snapshotId: value.snapshotId as string | null } : {}),
		attemptId: value.attempt.id,
		generation: value.attempt.generation as number,
		takeGeneration: value.takeGeneration,
		contractVersion: 2,
		transport: "chunked-object",
		sourceMode: value.sourceMode as UploadV2Snapshot["sourceMode"],
		inventoryRevision: value.inventoryRevision as number,
		transferVerified: value.transferVerified,
		sourceSeal: value.sourceSeal as Record<string, unknown> | null,
		facts: value.facts as unknown as UploadV2Facts,
		...(value.processingWork !== undefined ? { processingWork: value.processingWork as UploadV2Snapshot["processingWork"] } : {})
	};
}
export function parseUploadV2ObjectMappings(value: unknown): UploadV2ObjectMapping[] {
	if (!Array.isArray(value) || value.length > 32) invalid("object mapping batch");
	const ids = new Set<string>(),
		clients = new Set<string>();
	return value.map((mapping) => {
		if (
			!record(mapping) ||
			!uuid(mapping.clientObjectId) ||
			!uuid(mapping.objectId) ||
			typeof mapping.manifestPending !== "boolean" ||
			ids.has(mapping.objectId) ||
			clients.has(mapping.clientObjectId)
		)
			invalid("object mapping identity");
		ids.add(mapping.objectId);
		clients.add(mapping.clientObjectId);
		return { clientObjectId: mapping.clientObjectId, objectId: mapping.objectId, manifestPending: mapping.manifestPending };
	});
}
export function parseUploadV2ObjectTargets(
	value: unknown,
	fence: UploadObjectFence,
	expected: readonly { objectId: string; part: UploadObjectPartDescriptor }[]
): UploadV2ObjectTarget[] {
	if (!Array.isArray(value) || value.length > 100) invalid("target batch");
	const requested = new Map(expected.map((target) => [`${target.objectId}:${target.part.ordinal}`, target.part]));
	const seen = new Set<string>();
	return value.map((target) => {
		if (!record(target) || !uuid(target.objectId)) invalid("target object identity");
		const key = `${target.objectId}:${target.ordinal}`,
			part = requested.get(key);
		if (part && !/^[a-f0-9]{64}$/.test(part.sha256)) invalid("local part checksum");
		if (
			!part ||
			seen.has(key) ||
			target.attemptId !== fence.attemptId ||
			target.generation !== fence.generation ||
			target.targetId !== `${fence.attemptId}:${key}` ||
			target.expectedBytes !== part.byteSize ||
			target.expectedDigest !== part.sha256 ||
			!isValidPresignedUrl(target.url) ||
			typeof target.expiresAt !== "string" ||
			!Number.isFinite(Date.parse(target.expiresAt)) ||
			!isValidBrowserSignedHeaders(target.headers)
		)
			invalid("target differs from the immutable part");
		const headers = Object.fromEntries(Object.entries(target.headers).map(([name, header]) => [name.toLowerCase(), header]));
		const checksum = btoa(String.fromCharCode(...part.sha256.match(/../g)!.map((byte) => parseInt(byte, 16))));
		if (
			headers["if-none-match"] !== "*" ||
			headers["x-amz-checksum-sha256"] !== checksum ||
			headers["x-amz-meta-skip-processing"] !== "true"
		)
			invalid("target lacks checksum-bound write-once headers");
		seen.add(key);
		return target as unknown as UploadV2ObjectTarget;
	});
}

export interface UploadV2PartReceipt {
	objectId: string;
	ordinal: number;
	verified: true;
	byteSize: number;
	sha256: string;
	eTag: string;
}
export function parseUploadV2PartReceipt(value: unknown, objectId: string, part: UploadObjectPartDescriptor): UploadV2PartReceipt {
	if (
		!record(value) ||
		value.objectId !== objectId ||
		value.ordinal !== part.ordinal ||
		value.verified !== true ||
		value.byteSize !== part.byteSize ||
		value.sha256 !== part.sha256 ||
		!isValidEtag(value.eTag)
	)
		invalid("part receipt differs from its immutable bytes");
	return { objectId, ordinal: part.ordinal, verified: true, byteSize: part.byteSize, sha256: part.sha256, eTag: value.eTag };
}

/** Discovery cannot authorize a saved source or change its persisted contract. */
export function parseUploadCapabilities(value: unknown): UploadCapabilities {
	if (
		!record(value) ||
		!Array.isArray(value.contractVersions) ||
		!value.contractVersions.every((v) => Number.isSafeInteger(v) && v > 0) ||
		![1, 2].includes(value.preferredContractVersion as number) ||
		!value.contractVersions.includes(value.preferredContractVersion) ||
		!record(value.v2)
	)
		invalid("capabilities versions");
	const v2 = value.v2;
	if (
		typeof v2.newUploadsAllowed !== "boolean" ||
		v2.requiredTransport !== "chunked-object" ||
		!Array.isArray(v2.kinds) ||
		!v2.kinds.every((v) => typeof v === "string" && v.length > 0) ||
		!Array.isArray(v2.sourceModes) ||
		!v2.sourceModes.every((v) => v === "known-size" || v === "open") ||
		!Array.isArray(v2.checksumAlgorithms) ||
		!v2.checksumAlgorithms.includes("SHA-256") ||
		!v2.checksumAlgorithms.every((v) => typeof v === "string") ||
		!record(v2.limits) ||
		!Number.isSafeInteger(v2.domainByteLimit) ||
		(v2.domainByteLimit as number) < 1
	)
		invalid("capabilities policy");
	for (const key of [
		"minNonFinalPartBytes",
		"maxPartBytes",
		"maxObjectBytes",
		"maxPartsPerObject",
		"maxObjectsPerAdmission",
		"maxPartDescriptorsPerAdmission",
		"maxTargetsPerRequest",
		"maxObjectsPerPage",
		"maxPartsPerPage",
		"maxAdmissionBodyBytes"
	]) {
		if (!Number.isSafeInteger(v2.limits[key]) || (v2.limits[key] as number) < 1) invalid("capabilities limits");
	}
	if (
		(v2.limits.minNonFinalPartBytes as number) > (v2.limits.maxPartBytes as number) ||
		(v2.limits.maxPartBytes as number) > (v2.limits.maxObjectBytes as number) ||
		(v2.domainByteLimit as number) > (v2.limits.maxObjectBytes as number) ||
		(value.preferredContractVersion === 2 && (!v2.newUploadsAllowed || !value.contractVersions.includes(2)))
	)
		invalid("inconsistent capabilities");
	return value as unknown as UploadCapabilities;
}

/** A processing subset never changes the server's pending total or media readiness. */
export function remainingUploadTransferCount(pending: number, processing?: unknown): number | undefined {
	if (!Number.isFinite(pending) || pending < 0) return undefined;
	if (processing === undefined) return pending;
	if (typeof processing !== "number" || !Number.isInteger(processing) || processing < 0 || processing > pending) return undefined;
	return pending - processing;
}
